import React from 'react';

import {Page, StyleSheet, Text, View} from '@react-pdf/renderer';
import {Style} from '@react-pdf/types';

const styles = StyleSheet.create({
	viewSection: {
		padding: '16px',
	},
	viewLine: {
		flexDirection: 'row',
		paddingVertical: '8px',
		borderBottom: '1px solid #d3d3df',
	},
	viewLineNoBorder: {
		flexDirection: 'row',
		paddingVertical: '8px',
	},
	viewRow: {
		padding: '16px',
		flexDirection: 'row',
	},
	viewColumn: {
		flexDirection: 'column',
	},
	textMain: {
		color: '#232334',
		fontSize: '10px',
		fontFamily: 'Helvetica',
	},
	textMainBold: {
		color: '#232334',
		fontSize: '10px',
		fontFamily: 'Helvetica-Bold',
	},
	textAuxiliary: {
		color: '#6b6d7b',
		fontFamily: 'Helvetica-Bold',
		fontSize: '10px',
	},
	textHeader: {
		color: '#0004db',
		fontSize: '20px',
		fontFamily: 'Helvetica-Bold',
	},
	textHeaderSecondary: {
		color: '#0004db',
		fontSize: '16px',
		fontFamily: 'Helvetica-Bold',
	},
	textSubHeader: {
		color: '#0004db',
		fontSize: '14px',
		fontFamily: 'Helvetica-Bold',
	},
	textTotal: {
		color: '#0004db',
		fontSize: '12px',
		fontFamily: 'Helvetica-Bold',
	},
});

type LayoutComponent = React.PropsWithChildren<{
	style?: Style;
}>;

/**
 * These components could likely be re-used across future PDFs as well. Move to a global context if so.
 */

export const PageStandard = ({style = {}, children}: LayoutComponent): JSX.Element => {
	return (
		<Page size="A4" style={[{paddingVertical: '24px', paddingHorizontal: '16px'}, style]}>
			{children}
		</Page>
	);
};

interface TextMainProps extends LayoutComponent {
	bold?: boolean;
}
export const TextMain = ({bold = false, style = {}, children}: TextMainProps): JSX.Element => {
	const textStyle = bold ? styles.textMainBold : styles.textMain;
	return <Text style={[textStyle, style]}>{children}</Text>;
};

export const TextAuxiliary = ({style = {}, children}: LayoutComponent): JSX.Element => {
	return <Text style={[styles.textAuxiliary, style]}>{children}</Text>;
};

export const TextHeader = ({style = {}, children}: LayoutComponent): JSX.Element => {
	return <Text style={[styles.textHeader, style]}>{children}</Text>;
};

export const TextHeaderSecondary = ({style = {}, children}: LayoutComponent): JSX.Element => {
	return <Text style={[styles.textHeaderSecondary, style]}>{children}</Text>;
};

export const TextSubHeader = ({style = {}, children}: LayoutComponent): JSX.Element => {
	return <Text style={[styles.textSubHeader, style]}>{children}</Text>;
};

export const TextTotal = ({style = {}, children}: LayoutComponent): JSX.Element => {
	return <Text style={[styles.textTotal, style]}>{children}</Text>;
};

export const ViewSection = ({style = {}, children}: LayoutComponent): JSX.Element => {
	return <View style={[styles.viewSection, style]}>{children}</View>;
};

interface ViewRowProps extends LayoutComponent {
	fixed?: boolean;
}
export const ViewRow = ({style = {}, children, fixed = false}: ViewRowProps): JSX.Element => {
	return (
		<View fixed={fixed} style={[styles.viewRow, style]}>
			{children}
		</View>
	);
};

export const ViewColumn = ({style = {}, children}: LayoutComponent): JSX.Element => {
	return <View style={[styles.viewColumn, style]}>{children}</View>;
};

interface ViewLineProps extends LayoutComponent {
	noBorder?: boolean;
}
export const ViewLine = ({noBorder = false, style = {}, children}: ViewLineProps): JSX.Element => {
	const lineStyle = noBorder ? styles.viewLineNoBorder : styles.viewLine;
	return <View style={[lineStyle, style]}>{children}</View>;
};
